// Conflict-reconciliation surface (multi-tenant Phase 5). Renders the
// high-stakes `sync_conflicts` rows surfaced by @excellent/sync — money,
// pipeline stage, status, archival, and person/org identity divergences that
// must NEVER be silently last-write-wins. Reconciling one is "ship/verify
// applied to data": a human picks the winning value (this device's, the
// incoming peer's, or a typed merge), it is written onto the entity by ULID,
// and the conflict closes. The decision + persistence logic is in
// @excellent/sync; this block is purely the visual + interaction layer and
// receives the reconcile action as a prop (the shell owns the server action).

import { useState, useTransition } from "react";
import { TriangleAlert, CircleCheck, GitMerge } from "../../icons";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../../card";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Input } from "../../input";
import { cn } from "../../utils";

/** One open conflict awaiting reconciliation (mirrors @excellent/sync OpenConflict). */
export interface ConflictItem {
  uid: string;
  entity_table: string;
  row_uid: string;
  column_name: string;
  base_value: string | null;
  local_value: string | null;
  incoming_value: string | null;
  detected_at: string;
}

export type ResolveChoice = "local" | "incoming" | { value: string };

export interface ConflictQueueActions {
  /** Write the chosen value onto the entity and close the conflict. */
  resolveConflict: (args: {
    conflictUid: string;
    choose: ResolveChoice;
  }) => Promise<{ ok: boolean; reason?: string }>;
}

export interface ConflictQueueProps {
  conflicts: ConflictItem[];
  actions: ConflictQueueActions;
}

export function ConflictQueue({ conflicts, actions }: ConflictQueueProps) {
  // Seed from props; resolved conflicts are removed optimistically on success.
  const [items, setItems] = useState(conflicts);

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
          <CircleCheck strokeWidth={1.5} className="h-6 w-6 text-success" />
          <p className="type-body text-foreground">No open conflicts</p>
          <p className="max-w-sm type-subtext text-muted-foreground">
            Every high-stakes value across this workspace is reconciled. When two
            machines edit the same money, status, stage, or identity field at
            once, the divergence surfaces here instead of being silently
            overwritten.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((c) => (
        <ConflictCard
          key={c.uid}
          conflict={c}
          resolveConflict={actions.resolveConflict}
          onResolved={() =>
            setItems((prev) => prev.filter((x) => x.uid !== c.uid))
          }
        />
      ))}
    </div>
  );
}

function ConflictCard({
  conflict,
  resolveConflict,
  onResolved,
}: {
  conflict: ConflictItem;
  resolveConflict: ConflictQueueActions["resolveConflict"];
  onResolved: () => void;
}) {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const [mergeValue, setMergeValue] = useState("");

  function resolve(choose: ResolveChoice) {
    setError(null);
    startTransition(async () => {
      const res = await resolveConflict({ conflictUid: conflict.uid, choose });
      if (res.ok) onResolved();
      else setError(reasonLabel(res.reason));
    });
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="font-mono type-title">
            {conflict.entity_table}.{conflict.column_name}
          </CardTitle>
          <Badge variant="warning" className="gap-1">
            <TriangleAlert />
            high-stakes
          </Badge>
        </div>
        <CardDescription className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="font-mono type-subtext">row {conflict.row_uid}</span>
          <span aria-hidden>·</span>
          <span className="tabular-nums">detected {conflict.detected_at}</span>
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-px">
        <ValueRow label="Base (last agreed)" value={conflict.base_value} muted />
        <ChoiceRow
          label="This device"
          value={conflict.local_value}
          disabled={pending}
          onKeep={() => resolve("local")}
        />
        <ChoiceRow
          label="Incoming peer"
          value={conflict.incoming_value}
          disabled={pending}
          onKeep={() => resolve("incoming")}
        />
      </CardContent>

      <CardFooter className="flex flex-col items-stretch gap-2">
        {merging ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              resolve({ value: mergeValue });
            }}
          >
            <Input
              autoFocus
              value={mergeValue}
              onChange={(e) => setMergeValue(e.target.value)}
              placeholder="Enter the value to keep…"
              disabled={pending}
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={pending || mergeValue === ""}>
              Apply merge
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending}
              onClick={() => {
                setMerging(false);
                setMergeValue("");
              }}
            >
              Cancel
            </Button>
          </form>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={pending}
            className="self-start text-muted-foreground"
            onClick={() => setMerging(true)}
          >
            <GitMerge strokeWidth={1.5} />
            Enter a merged value instead
          </Button>
        )}
        {error && <p className="type-subtext text-destructive">{error}</p>}
      </CardFooter>
    </Card>
  );
}

/** A read-only comparison row (e.g. the base value for context). */
function ValueRow({
  label,
  value,
  muted,
}: {
  label: string;
  value: string | null;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0">
      <span className="shrink-0 type-subtext text-muted-foreground">{label}</span>
      <ValueText value={value} muted={muted} className="min-w-0 flex-1 text-right" />
    </div>
  );
}

/** A selectable comparison row with a "Keep" action. */
function ChoiceRow({
  label,
  value,
  disabled,
  onKeep,
}: {
  label: string;
  value: string | null;
  disabled: boolean;
  onKeep: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="type-subtext text-muted-foreground">{label}</span>
        <ValueText value={value} />
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={onKeep}
        className="shrink-0"
      >
        Keep
      </Button>
    </div>
  );
}

function ValueText({
  value,
  muted,
  className,
}: {
  value: string | null;
  muted?: boolean;
  className?: string;
}) {
  if (value === null || value === "") {
    return (
      <span className={cn("block truncate font-mono type-subtext italic text-muted-foreground", className)}>
        ∅ empty
      </span>
    );
  }
  return (
    <span
      className={cn(
        "block truncate font-mono type-body",
        muted ? "text-muted-foreground" : "text-foreground",
        className,
      )}
      title={value}
    >
      {value}
    </span>
  );
}

function reasonLabel(reason?: string): string {
  switch (reason) {
    case "already-resolved":
      return "This conflict was already resolved on another device.";
    case "unsafe-identifier":
      return "Refused: the conflict references an unrecognized table or column.";
    case "merged-value-required":
      return "Enter a value to merge.";
    default:
      return "Could not resolve this conflict. Please retry.";
  }
}
